// Top of every Today/Past Day screen.
// Toolbar row (prev/next day + menu) above an identity row (avatar + name + age + weight chip).

import { BabyAvatar } from "./BabyAvatar";
import { IconChevron, IconMenu } from "./icons";
import { localDateLabel, isSameLocalDay } from "../lib/timeFmt";
import { ageFromBirthday, formatAge } from "../lib/age";
import { gramsToLbOz, lastWeight } from "../lib/events";
import type { EventRow } from "../lib/events";
import { useT } from "../lib/LocaleContext";

type Props = {
  babyName: string;
  birthday: string;
  dueDate?: string | null;
  events: EventRow[];
  viewDate: Date;
  onPrevDay?: () => void;
  onNextDay?: () => void;
  onMenu?: () => void;
  onWeightChip?: () => void;
  onNameClick?: () => void;
  photoDataUrl?: string | null;
  onPhotoChange?: (dataUrl: string) => Promise<void> | void;
};

export function Header({
  babyName,
  birthday,
  dueDate,
  events,
  viewDate,
  onPrevDay,
  onNextDay,
  onMenu,
  onWeightChip,
  onNameClick,
  photoDataUrl,
  onPhotoChange,
}: Props) {
  const t = useT();
  const today = new Date();
  const isToday = isSameLocalDay(viewDate, today);
  const atBirthday = isSameLocalDay(viewDate, new Date(`${birthday}T12:00:00`));
  const dateLabel = isToday ? t.header.todayDate(localDateLabel(viewDate)) : localDateLabel(viewDate);
  // Preemies track on adjusted age (from due date) — label it so it isn't mistaken
  // for chronological age.
  const adjustedAge = !!dueDate && dueDate !== birthday;
  const age = formatAge(ageFromBirthday(dueDate ?? birthday, viewDate)) + (adjustedAge ? t.header.adjustedSuffix : "");
  const weightRow = lastWeight(events);

  return (
    <div style={styles.header}>
      <div style={styles.toolbar}>
        <button
          style={{ ...styles.navBtn, opacity: atBirthday ? 0.3 : 1 }}
          disabled={atBirthday}
          onClick={onPrevDay}
          aria-label={t.header.prevDay}
        >
          <IconChevron size={18} dir="left" />
        </button>
        <div style={styles.dateCenter}>{dateLabel}</div>
        <button
          style={{ ...styles.navBtn, opacity: isToday ? 0.3 : 1 }}
          disabled={isToday}
          onClick={onNextDay}
          aria-label={t.header.nextDay}
        >
          <IconChevron size={18} dir="right" />
        </button>
        <button style={styles.menuBtn} onClick={onMenu} aria-label={t.header.menu}>
          <IconMenu size={20} />
        </button>
      </div>

      <div style={styles.idRow}>
        <BabyAvatar size={56} name={babyName} photoDataUrl={photoDataUrl} editable={!!onPhotoChange} onPhotoChange={onPhotoChange} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1
            className="bh-id-name"
            style={{ ...styles.name, cursor: onNameClick ? "pointer" : "default" }}
            onClick={onNameClick}
            role={onNameClick ? "button" : undefined}
            aria-label={onNameClick ? t.header.aboutBaby(babyName) : undefined}
          >
            {babyName}
          </h1>
          <div style={styles.metaRow}>
            <span className="bh-num" style={styles.ageMeta}>
              {age}
            </span>
            <span style={styles.metaDot}>·</span>
            <WeightChip eventRow={weightRow} onClick={onWeightChip} />
          </div>
        </div>
      </div>
    </div>
  );
}

function WeightChip({ eventRow, onClick }: { eventRow: EventRow | null; onClick?: () => void }) {
  const t = useT();
  if (!eventRow || eventRow.weight_grams == null) {
    return (
      <button style={styles.weightChip} onClick={onClick}>
        <span style={styles.weightDot} />
        <span style={{ fontWeight: 600, color: "var(--bh-ink-3)" }}>{t.header.addWeight}</span>
      </button>
    );
  }
  const { lb, oz } = gramsToLbOz(eventRow.weight_grams);
  return (
    <button style={styles.weightChip} onClick={onClick}>
      <span style={styles.weightDot} />
      <span className="bh-num" style={{ fontWeight: 600, color: "var(--bh-ink-2)" }}>
        {t.header.weightValue(lb, oz)}
      </span>
    </button>
  );
}

const styles: Record<string, React.CSSProperties> = {
  header: { padding: "6px 14px 14px" },
  toolbar: { display: "flex", alignItems: "center", gap: 4, padding: "0 0 8px" },
  navBtn: {
    width: 36,
    height: 36,
    borderRadius: 999,
    background: "transparent",
    border: "none",
    color: "var(--bh-ink-2)",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dateCenter: {
    flex: 1,
    textAlign: "center",
    fontSize: 14,
    fontWeight: 600,
    color: "var(--bh-ink-2)",
  },
  menuBtn: {
    width: 36,
    height: 36,
    borderRadius: 999,
    background: "var(--bh-surface)",
    border: "none",
    color: "var(--bh-ink-2)",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  idRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 14,
    padding: "4px 6px 0",
  },
  name: {
    fontFamily: "var(--bh-font-serif)",
    fontSize: 34,
    fontWeight: 500,
    letterSpacing: "-0.02em",
    lineHeight: 1,
    margin: 0,
    color: "var(--bh-ink)",
  },
  metaRow: { display: "flex", alignItems: "center", gap: 8, marginTop: 4 },
  ageMeta: { fontSize: 14, color: "var(--bh-ink-3)" },
  metaDot: { color: "var(--bh-ink-4)", fontSize: 12 },
  weightChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "var(--bh-surface)",
    border: "1px solid var(--bh-divider)",
    color: "var(--bh-ink-2)",
    padding: "4px 10px 4px 8px",
    borderRadius: 999,
    cursor: "pointer",
    fontSize: 12,
  },
  weightDot: {
    width: 6,
    height: 6,
    borderRadius: 999,
    background: "var(--bh-attn)",
  },
};
